/**
 * Identificativo del client per il rate limiting.
 *
 * E' un UUID casuale generato dal browser, non un fingerprint del dispositivo:
 * nessuna lettura di canvas, WebGL, font o audio. Le tecniche di fingerprinting
 * hardware sono dato personale ai sensi del GDPR e richiedono una base
 * giuridica piu' onerosa del legittimo interesse anti-abuso, sono comunque
 * aggirabili, e il backend applica in ogni caso un limite per IP che copre il
 * caso in cui questo valore venga cancellato o falsificato.
 *
 * Il valore non identifica la persona: e' casuale, non contiene dati, e lato
 * server viene salato e sottoposto ad hash prima di essere usato come chiave.
 */

const STORAGE_KEY = 'cv-chat-client-id';

/** Fallback quando ogni forma di storage e' inaccessibile (Safari privato, iframe). */
let memoryId: string | null = null;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback su getRandomValues per browser senza randomUUID: il backend accetta
  // solo UUID v4 ben formati, quindi il formato va rispettato.
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // versione 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Ultima spiaggia: senza crypto il valore non e' robusto, ma la protezione
  // reale resta il limite per IP applicato dal server.
  const rnd = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, '0');
  return `${rnd()}${rnd()}-${rnd()}-4${rnd().slice(1)}-8${rnd().slice(1)}-${rnd()}${rnd()}${rnd()}`;
};

const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (value: string): boolean => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
    return true;
  } catch {
    return false;
  }
};

/** Restituisce l'ID del client, creandolo alla prima chiamata. */
export function getClientId(): string {
  const existing = readStored();
  if (existing) return existing;

  if (memoryId) return memoryId;

  const created = generateId();
  if (!writeStored(created)) {
    // Storage non disponibile: l'ID vive per la durata della pagina.
    memoryId = created;
  }
  return created;
}
